// src/api/salonConfigService.js - MIGRATED to Flat Hierarchy (salons collection)

import { 
  doc, 
  getDoc, 
  setDoc,
  updateDoc,
  serverTimestamp,
  collection,
  query,
  where,
  getDocs
} from 'firebase/firestore';
import { db } from '../utils/firebase';

/**
 * Get salon configuration - Uses 'salons' collection
 */
export const getSalonConfig = async (salonId) => {
  if (!salonId) {
    console.error('No salon ID provided');
    throw new Error('Salon ID is required');
  }

  try {
    console.log('Fetching salon configuration for:', salonId);

    const salonRef = doc(db, 'salons', salonId);
    const salonDoc = await getDoc(salonRef);

    if (salonDoc.exists()) {
      console.log('✓ Found in salons collection');
      const config = salonDoc.data();

      // Convert flat structure to legacy format for backward compatibility
      const legacyFormat = {
        salonId: config.salonId || salonId,
        branding: config.branding || {},
        contact: {
          address: config.address || '',
          phone: config.phone || '',
          email: config.email || '',
          socialMedia: config.socialMedia || {}
        },
        businessHours: config.businessHours || {},
        aboutUs: config.aboutUs || '',
        autoApproveBookings: config.autoApproveBookings || false,
        createdAt: config.createdAt,
        updatedAt: config.updatedAt
      };

      // Process business hours to handle mixed formats
      if (legacyFormat.businessHours) {
        const businessHours = legacyFormat.businessHours;
        const numericKeys = Object.keys(businessHours).filter(key => !isNaN(key));

        if (numericKeys.length > 0 && !businessHours.regularHours) {
          console.log("Converting numeric keys to regularHours array");
          const regularHours = numericKeys
            .sort((a, b) => parseInt(a) - parseInt(b))
            .map(key => businessHours[key]);

          legacyFormat.businessHours = {
            regularHours: regularHours,
            holidayClosures: businessHours.holidayClosures || [],
            customClosures: businessHours.customClosures || []
          };
        } else if (!businessHours.regularHours && !businessHours.holidayClosures && !businessHours.customClosures) {
          console.log("Converting old array format to new structure");
          legacyFormat.businessHours = {
            regularHours: Array.isArray(businessHours) ? businessHours : [],
            holidayClosures: [],
            customClosures: []
          };
        }
      }

      return legacyFormat;
    }

    console.warn(`✗ No configuration found for salon: ${salonId}`);
    
    // Return default configuration instead of null
    return {
      salonId: salonId,
      branding: {
        name: 'Beauty Salon',
        logo: null,
        colors: {
          primary: '#b6a593',
          secondary: '#6b5b4d',
          accent: '#d9c5b2',
          background: '#FFFFFF',
          text: '#333333'
        }
      },
      contact: {
        address: 'Address not set',
        phone: 'Phone not set',
        email: 'Email not set',
        socialMedia: {}
      },
      businessHours: {
        regularHours: [
          { day: 'Monday', open: '09:00', close: '18:00', isClosed: false },
          { day: 'Tuesday', open: '09:00', close: '18:00', isClosed: false },
          { day: 'Wednesday', open: '09:00', close: '18:00', isClosed: false },
          { day: 'Thursday', open: '09:00', close: '18:00', isClosed: false },
          { day: 'Friday', open: '09:00', close: '18:00', isClosed: false },
          { day: 'Saturday', open: '10:00', close: '16:00', isClosed: false },
          { day: 'Sunday', open: '00:00', close: '00:00', isClosed: true }
        ],
        holidayClosures: [],
        customClosures: []
      },
      aboutUs: ''
    };
  } catch (error) {
    console.error('Error fetching salon config:', error);
    throw error;
  }
};

/**
 * Update salon configuration - Saves to 'salons' collection
 */
export const updateSalonConfig = async (salonId, configData) => {
  try {
    if (!salonId || !configData) {
      throw new Error('Invalid salon ID or configuration data');
    }

    console.log("Saving configuration to salons collection for:", salonId);

    // Prepare and validate data
    const dataToSave = { ...configData };

    // Validate and normalize business hours structure
    if (dataToSave.businessHours) {
      console.log("Validating business hours structure before save");

      if (Array.isArray(dataToSave.businessHours)) {
        console.log("Converting array format to new object structure");
        dataToSave.businessHours = {
          regularHours: dataToSave.businessHours,
          holidayClosures: [],
          customClosures: []
        };
      } else if (dataToSave.businessHours && typeof dataToSave.businessHours === 'object') {
        if (!dataToSave.businessHours.regularHours) {
          dataToSave.businessHours.regularHours = [];
        }
        if (!dataToSave.businessHours.holidayClosures) {
          dataToSave.businessHours.holidayClosures = [];
        }
        if (!dataToSave.businessHours.customClosures) {
          dataToSave.businessHours.customClosures = [];
        }
      }
    }

    const salonRef = doc(db, 'salons', salonId);
    const salonDoc = await getDoc(salonRef);

    if (!salonDoc.exists()) {
      // Create new salon document with flat structure
      const newSalonData = {
        clientId: dataToSave.clientId || 'demo-client',
        salonId: salonId,
        name: dataToSave.branding?.name || 'Unknown Salon',
        subdomain: salonId,
        customDomain: null,

        // Extract contact info from nested structure
        address: dataToSave.contact?.address || '',
        phone: dataToSave.contact?.phone || '',
        email: dataToSave.contact?.email || '',

        // Settings
        settings: dataToSave.settings || {
          timezone: 'Asia/Singapore',
          currency: 'SGD',
          language: 'en'
        },

        // Keep branding as nested object
        branding: dataToSave.branding || {},

        // Extract social media
        socialMedia: dataToSave.contact?.socialMedia || {},

        // Business hours and content
        businessHours: dataToSave.businessHours || {},
        aboutUs: dataToSave.aboutUs || '',

        status: 'active',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      await setDoc(salonRef, newSalonData);
      console.log("✓ Created new salon in salons collection");
    } else {
      // Update existing salon - convert to flat hierarchy
      const updateData = {
        // Update root-level contact fields
        address: dataToSave.contact?.address || salonDoc.data().address,
        phone: dataToSave.contact?.phone || salonDoc.data().phone,
        email: dataToSave.contact?.email || salonDoc.data().email,

        // Update nested objects
        branding: dataToSave.branding || salonDoc.data().branding,
        socialMedia: dataToSave.contact?.socialMedia || salonDoc.data().socialMedia,
        businessHours: dataToSave.businessHours || salonDoc.data().businessHours,
        aboutUs: dataToSave.aboutUs || salonDoc.data().aboutUs,

        updatedAt: serverTimestamp()
      };

      await setDoc(salonRef, updateData, { merge: true });
      console.log("✓ Updated salon in salons collection");
    }

    console.log("✓ Salon configuration saved successfully");
    return true;
  } catch (error) {
    console.error('Error updating salon config:', error);
    throw error;
  }
};

/**
 * Partial update of salon configuration - Uses 'salons' collection
 */
export const partialUpdateSalonConfig = async (salonId, updateData) => {
  try {
    if (!salonId) {
      throw new Error('Salon ID is required');
    }

    console.log("Partially updating salon:", salonId);

    const salonRef = doc(db, 'salons', salonId);
    await updateDoc(salonRef, {
      ...updateData,
      updatedAt: serverTimestamp()
    });
    console.log("✓ Partially updated in salons collection");

    return true;
  } catch (error) {
    console.error('Error partially updating salon config:', error);
    throw error;
  }
};

/**
 * Get all salon configurations - Queries 'salons' collection
 */
export const getAllSalonConfigurations = async () => {
  try {
    console.log("Fetching all salon configurations");

    const salonsRef = collection(db, 'salons');
    const salonsSnapshot = await getDocs(salonsRef);

    const salons = salonsSnapshot.docs.map(doc => {
      const data = doc.data();
      // Convert to legacy format for backward compatibility
      return {
        id: doc.id,
        salonId: data.salonId || doc.id,
        branding: data.branding || {},
        contact: {
          address: data.address || '',
          phone: data.phone || '',
          email: data.email || '',
          socialMedia: data.socialMedia || {}
        },
        businessHours: data.businessHours || {},
        aboutUs: data.aboutUs || '',
        createdAt: data.createdAt,
        updatedAt: data.updatedAt
      };
    });

    console.log(`✓ Found ${salons.length} salons`);
    return salons;
  } catch (error) {
    console.error('Error fetching all salon configurations:', error);
    throw error;
  }
};

/**
 * Search salons by name or location - Searches 'salons' collection
 */
export const searchSalons = async (searchTerm) => {
  try {
    if (!searchTerm) {
      throw new Error('Search term is required');
    }

    console.log("Searching salons for:", searchTerm);
    const searchLower = searchTerm.toLowerCase();

    const salonsRef = collection(db, 'salons');
    const salonsSnapshot = await getDocs(salonsRef);

    const salons = [];

    salonsSnapshot.forEach(doc => {
      const data = doc.data();
      const salonName = (data.name || data.branding?.name || '').toLowerCase();
      const address = (data.address || '').toLowerCase();

      if (salonName.includes(searchLower) || address.includes(searchLower)) {
        // Convert to legacy format
        salons.push({
          id: doc.id,
          salonId: data.salonId || doc.id,
          branding: data.branding || {},
          contact: {
            address: data.address || '',
            phone: data.phone || '',
            email: data.email || '',
            socialMedia: data.socialMedia || {}
          }
        });
      }
    });

    console.log(`✓ Found ${salons.length} matching salons`);
    return salons;
  } catch (error) {
    console.error('Error searching salons:', error);
    throw error;
  }
};

/**
 * Validate salon configuration (unchanged - works with both structures)
 */
export const validateSalonConfig = (config) => {
  const errors = [];

  // Check branding
  if (!config.branding || !config.branding.name) {
    errors.push('Salon name is required');
  }

  // Check contact information
  if (!config.contact || !config.contact.email) {
    errors.push('Contact email is required');
  }

  // Check business hours with new structure
  if (!config.businessHours) {
    errors.push('Business hours must be configured');
  } else if (config.businessHours.regularHours && config.businessHours.regularHours.length === 0) {
    errors.push('Regular business hours must be configured');
  } else if (Array.isArray(config.businessHours) && config.businessHours.length === 0) {
    errors.push('Business hours must be configured');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Check if salon config exists - Checks 'salons' collection
 */
export const salonConfigExists = async (salonId) => {
  if (!salonId) {
    console.error('No salon ID provided');
    return false;
  }

  try {
    const salonRef = doc(db, 'salons', salonId);
    const salonDoc = await getDoc(salonRef);

    if (salonDoc.exists()) {
      console.log('✓ Salon exists');
      return true;
    }

    console.log('✗ Salon does not exist');
    return false;
  } catch (error) {
    console.error('Error checking salon config existence:', error);
    return false;
  }
};